import { useRef, useState, useCallback } from "react";
import { Upload } from "lucide-react";

/**
 * DropZone — small reusable wrapper that adds native HTML5 drag & drop to any area.
 *
 * Props:
 *  - onFiles(File[])   — invoked with the array of dropped files
 *  - disabled          — disables the drop capture (still renders children)
 *  - accept            — optional MIME hint text shown in the overlay (e.g. "images, PDF")
 *  - className         — passed through
 *  - overlayLabel      — text on the drop overlay (default: "Drop to upload")
 *  - testId            — data-testid for the wrapping element
 *  - children          — the visual content underneath the dropzone
 */
export default function DropZone({
  onFiles,
  disabled = false,
  accept = "",
  className = "",
  overlayLabel = "Drop to upload",
  testId = "dropzone",
  children,
}) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);

  const onDragEnter = useCallback((e) => {
    if (disabled) return;
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault(); e.stopPropagation();
    depth.current += 1;
    setOver(true);
  }, [disabled]);

  const onDragOver = useCallback((e) => {
    if (disabled) return;
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault(); e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  }, [disabled]);

  const onDragLeave = useCallback((e) => {
    if (disabled) return;
    e.preventDefault(); e.stopPropagation();
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setOver(false);
  }, [disabled]);

  const onDrop = useCallback((e) => {
    if (disabled) return;
    e.preventDefault(); e.stopPropagation();
    depth.current = 0;
    setOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0 && onFiles) onFiles(files);
  }, [disabled, onFiles]);

  return (
    <div
      className={`relative ${className}`}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid={testId}
    >
      {children}
      {over && (
        <div
          className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center border-2 border-dashed border-[#0A0A0A] bg-white/85 backdrop-blur-sm"
          data-testid={`${testId}-overlay`}
        >
          <div className="flex flex-col items-center gap-2 text-[#0A0A0A]">
            <Upload size={28} strokeWidth={1.5}/>
            <div className="text-xs uppercase tracking-widest">{overlayLabel}</div>
            {accept && <div className="text-[10px] text-[#525252]">{accept}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
